import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { STATE_MAPPINGS } from "@/lib/stateUtils";
import { isCrawlableSalaryLink } from "@/lib/seoIndexing";

interface RelatedStatesProps {
  occupationSlug: string;
  currentState?: string;
  maxLinks?: number;
}

/**
 * Links to same occupation in other states for SEO internal linking.
 * Uses rel="nofollow" for non-indexed occupation/state combinations.
 */
export default function RelatedStates({
  occupationSlug,
  currentState,
  maxLinks = 5
}: RelatedStatesProps) {
  const topStates = [
    'california', 'texas', 'florida', 'new-york', 'pennsylvania',
    'illinois', 'ohio', 'georgia', 'north-carolina', 'michigan'
  ];

  const relatedStates = topStates
    .filter(state => state !== currentState)
    .slice(0, maxLinks)
    .map(slug => STATE_MAPPINGS.find(s => s.slug === slug))
    .filter(Boolean);

  if (relatedStates.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          {occupationSlug ? `${occupationSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} in Other States` : 'Explore Other States'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Compare salaries for this occupation across different states:
        </p>
        <ul className="space-y-2">
          {relatedStates.map((state) => {
            const crawlable = isCrawlableSalaryLink(occupationSlug, state!.slug);
            return (
              <li key={state!.slug}>
                <Link
                  to={`/salary/${occupationSlug}/${state!.slug}/`}
                  {...(!crawlable && { rel: 'nofollow' })}
                  className="text-primary hover:underline"
                >
                  {state!.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
